import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useBoardSocket } from '../lib/realtime';
import type {
  BoardDetail,
  IdeaWithReactions,
  Cluster,
  User,
  Phase,
} from '../../shared/types';
import type { ServerMessage } from '../../shared/messages';
import { Link, useRouter } from '../router';
import { AvatarStack } from '../components/Avatar';
import { UiNode } from '../lib/UiNode';
import { IconArrowLeft, IconShare, IconChat, IconBell, IconBellOff, IconCog } from '../components/Icon';
import { BoardSettingsSheet } from '../components/BoardSettingsSheet';
import { ShareSheet } from '../components/ShareSheet';
import { LobbyInbox } from '../components/LobbyInbox';
import { phaseLabel, nextPhase, ALL_PHASES } from '../lib/phases';
import { applyTheme } from '../lib/themes';
import { CapturePhase } from '../phases/CapturePhase';
import { ReactPhase } from '../phases/ReactPhase';
import { ClusterPhase } from '../phases/ClusterPhase';
import { PlanPhase } from '../phases/PlanPhase';
import { DeckPhase } from '../phases/DeckPhase';
import { SetupPhase } from '../phases/SetupPhase';
import { MyTasksStrip } from '../components/MyTasksStrip';
import { IntentPanel } from '../components/IntentPanel';
import type { PlanState } from '../../shared/types';

export function BoardView({ boardId, user }: { boardId: string; user: User }) {
  const [board, setBoard] = useState<BoardDetail | null>(null);
  const [ideas, setIdeas] = useState<IdeaWithReactions[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [planState, setPlanState] = useState<PlanState>({
    days: [],
    sessions: [],
    availability: [],
    chat: [],
  });
  const [planLoading, setPlanLoading] = useState(true);
  /**
   * Which scenario is currently being viewed. Null = the canonical
   * plan. Lives at this level so the WebSocket handler can filter
   * inbound session events — if a teammate edits a session on a
   * different scenario we ignore the event for our local state.
   */
  const [activeBranchId, setActiveBranchId] = useState<string | null>(null);
  // Ref mirrors the state so the stable useCallback handler below can
  // read the latest value without re-subscribing on every change.
  const activeBranchRef = useRef<string | null>(null);
  useEffect(() => {
    activeBranchRef.current = activeBranchId;
  }, [activeBranchId]);
  const [error, setError] = useState<string | null>(null);
  const { navigate } = useRouter();

  // Initial fetches.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.board(boardId),
      api.ideas(boardId),
      api.clusters(boardId),
      api.planState(boardId).catch(() => null),
    ])
      .then(([b, i, cl, ps]) => {
        if (cancelled) return;
        setBoard(b);
        setIdeas(i.ideas);
        setClusters(cl.clusters);
        if (ps) setPlanState(ps);
        setPlanLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  // Apply per-board visual theme on enter; reset on leave. User
  // preference (if set) overrides — see resolveTheme.
  useEffect(() => {
    applyTheme({
      boardTheme: board?.visualTheme ?? null,
      boardAccentHue: board?.accentHue ?? null,
      userPreference: user.themePreference ?? null,
    });
    return () => applyTheme({ userPreference: user.themePreference ?? null });
  }, [board?.visualTheme, board?.accentHue, user.themePreference]);

  // Real-time updates.
  const handle = useCallback((msg: ServerMessage) => {
    switch (msg.type) {
      case 'idea.added':
        setIdeas((cur) => (cur.find((i) => i.id === msg.idea.id) ? cur : [...cur, msg.idea]));
        return;
      case 'idea.updated':
        setIdeas((cur) => cur.map((i) => (i.id === msg.idea.id ? msg.idea : i)));
        return;
      case 'idea.removed':
        setIdeas((cur) => cur.filter((i) => i.id !== msg.ideaId));
        return;
      case 'reaction.updated':
        setIdeas((cur) =>
          cur.map((i) => (i.id === msg.ideaId ? { ...i, reactions: msg.reactions } : i)),
        );
        return;
      case 'cluster.upserted':
        setClusters((cur) => {
          const others = cur.filter((c) => c.id !== msg.cluster.id);
          return [...others, msg.cluster].sort((a, b) => a.orderIndex - b.orderIndex);
        });
        return;
      case 'cluster.removed':
        setClusters((cur) => cur.filter((c) => c.id !== msg.clusterId));
        return;
      case 'idea.moved':
        setIdeas((cur) =>
          cur.map((i) => (i.id === msg.ideaId ? { ...i, clusterId: msg.clusterId } : i)),
        );
        return;
      case 'phase.changed':
        setBoard((cur) => (cur ? { ...cur, phase: msg.phase } : cur));
        return;
      case 'session.upserted': {
        // Ignore events for a scenario we aren't viewing.
        const mine = (msg.session.branchId ?? null) === activeBranchRef.current;
        if (!mine) return;
        setPlanState((cur) => {
          const others = cur.sessions.filter((s) => s.id !== msg.session.id);
          return { ...cur, sessions: [...others, msg.session] };
        });
        return;
      }
      case 'session.removed':
        if ((msg.branchId ?? null) !== activeBranchRef.current) return;
        setPlanState((cur) => ({
          ...cur,
          sessions: cur.sessions.filter((s) => s.id !== msg.sessionId),
        }));
        return;
      case 'plan.chat.message':
        setPlanState((cur) => {
          if (cur.chat.some((m) => m.id === msg.message.id)) return cur;
          return { ...cur, chat: [...cur.chat, msg.message] };
        });
        return;
      case 'plan.generated':
        // Trigger a fresh fetch — the broadcast doesn't include the full plan.
        void api.planState(boardId).then(setPlanState).catch(() => undefined);
        return;
      case 'activity': {
        // Generic activity → window CustomEvent so the right component
        // can refetch without prop-drilling a handler down the tree.
        const k = msg.activity.kind;
        if (k === 'comment.added' || k === 'comment.deleted') {
          window.dispatchEvent(
            new CustomEvent('synfolio:comment-changed', {
              detail: msg.activity.payload,
            }),
          );
        } else if (k === 'intent.updated' || k === 'intent.synth') {
          // Re-fetch the intent strip + summary. Auto-synth fires when
          // the last member submits, so every client needs to refresh
          // to see the freshly-generated summary appear.
          window.dispatchEvent(new CustomEvent('synfolio:intent-changed'));
        }
        return;
      }
    }
  }, [boardId]);

  const { state: socketState, presence } = useBoardSocket(boardId, handle);

  // After a reconnect, refetch the canonical state — broadcasts during the
  // disconnect won't have reached us. We track the previous state via a ref
  // so we only refetch on the closed→open transition, not on every render.
  const prevSocketStateRef = useRef<typeof socketState | null>(null);
  useEffect(() => {
    const prev = prevSocketStateRef.current;
    prevSocketStateRef.current = socketState;
    if (prev === 'closed' && socketState === 'open' && board) {
      void Promise.all([
        api.ideas(boardId),
        api.clusters(boardId),
        api.planState(boardId).catch(() => null),
      ]).then(([i, cl, ps]) => {
        setIdeas(i.ideas);
        setClusters(cl.clusters);
        if (ps) setPlanState(ps);
      });
    }
  }, [socketState, boardId, board]);

  const isOwner = board?.role === 'owner';
  const canAdvance = useMemo(() => isOwner && board && nextPhase(board.phase), [isOwner, board]);

  if (error) {
    return (
      <div className="panel flex flex-col items-center gap-3 p-8 text-center">
        <p>Couldn't open that board.</p>
        <p className="text-xs text-cream-3">{error}</p>
        <Link to="/" className="btn btn-ghost">
          Back home
        </Link>
      </div>
    );
  }

  if (!board) return <BoardSkeleton />;

  return (
    <UiNode
      name="board-view-screen"
      entity="board"
      entityId={board.id}
      state={board.phase}
      className="flex flex-col gap-5"
    >
      <BoardHeader
        board={board}
        presence={presence}
        socketState={socketState}
        canAdvance={!!canAdvance}
        me={user}
        onAdvance={async () => {
          const n = nextPhase(board.phase);
          if (!n) return;
          await api.setPhase(boardId, n);
          setBoard({ ...board, phase: n });
        }}
        onBack={() => navigate('/')}
        onBoardChange={setBoard}
      />

      <IntentPanel board={board} />

      <PhaseSurface
        board={board}
        ideas={ideas}
        clusters={clusters}
        planState={planState}
        planLoading={planLoading}
        activeBranchId={activeBranchId}
        onActiveBranchIdChange={setActiveBranchId}
        user={user}
        onIdeasChange={setIdeas}
        onClustersChange={setClusters}
        onBoardChange={setBoard}
        onPlanStateChange={setPlanState}
      />

      {/* Tasks moved BELOW the phase content. The phase is the thing
          you came to do; "for you" is supplemental and hides itself
          when there's nothing actionable. */}
      <MyTasksStrip boardId={board.id} />
    </UiNode>
  );
}

function PhaseSurface(props: {
  board: BoardDetail;
  ideas: IdeaWithReactions[];
  clusters: Cluster[];
  planState: PlanState;
  planLoading: boolean;
  activeBranchId: string | null;
  onActiveBranchIdChange: (next: string | null) => void;
  user: User;
  onIdeasChange: (
    cb: (cur: IdeaWithReactions[]) => IdeaWithReactions[],
  ) => void | ((next: IdeaWithReactions[]) => void);
  onClustersChange: (next: Cluster[] | ((cur: Cluster[]) => Cluster[])) => void;
  onBoardChange: (next: BoardDetail) => void;
  onPlanStateChange: (next: PlanState) => void;
}) {
  const { board, ideas, clusters, planState, planLoading, activeBranchId, onActiveBranchIdChange, user, onIdeasChange, onClustersChange, onBoardChange, onPlanStateChange } = props;
  switch (board.phase) {
    case 'setup':
      return <SetupPhase board={board} onBoardChange={onBoardChange} />;
    case 'capture':
      return <CapturePhase board={board} ideas={ideas} onIdeasChange={onIdeasChange} user={user} />;
    case 'react':
      return <ReactPhase board={board} ideas={ideas} />;
    case 'cluster':
      return (
        <ClusterPhase
          board={board}
          ideas={ideas}
          clusters={clusters}
          user={user}
          onClustersChange={onClustersChange}
        />
      );
    case 'schedule':
      return (
        <PlanPhase
          board={board}
          clusters={clusters}
          planState={planState}
          planLoading={planLoading}
          activeBranchId={activeBranchId}
          onActiveBranchIdChange={onActiveBranchIdChange}
          user={user}
          onPlanStateChange={onPlanStateChange}
        />
      );
    case 'shipped':
    case 'archived':
      return (
        <DeckPhase
          board={board}
          clusters={clusters}
          planState={planState}
          onBoardChange={onBoardChange}
        />
      );
  }
}

function BoardHeader({
  board,
  presence,
  socketState,
  canAdvance,
  me,
  onAdvance,
  onBack,
  onBoardChange,
}: {
  board: BoardDetail;
  presence: { id: string; name: string; avatarHue: number; avatarUrl: string | null }[];
  socketState: 'connecting' | 'open' | 'closed';
  canAdvance: boolean;
  me: User;
  onAdvance: () => void;
  onBack: () => void;
  onBoardChange: (next: BoardDetail) => void;
}) {
  const [shareOpen, setShareOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isOwner = me.id === board.members.find((m) => m.role === 'owner')?.id;
  const [lobbyOpen, setLobbyOpen] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <UiNode
          as="button"
          name="board-back-button"
          entity="board"
          entityId={board.id}
          action="close"
          className="btn btn-ghost -ml-2 inline-flex items-center gap-1.5"
          onClick={onBack}
        >
          <IconArrowLeft size={14} />
          <span>Back</span>
        </UiNode>
        <div className="flex items-center gap-2">
          <PresenceIndicator state={socketState} presence={presence} selfId={me.id} />
          {board.deckShareToken && (
            <UiNode
              as="button"
              name="board-lobby-button"
              entity="board"
              entityId={board.id}
              action="lobby_open"
              className="btn btn-ghost inline-flex items-center gap-1.5"
              onClick={() => setLobbyOpen(true)}
              title="Lobby feedback"
            >
              <IconChat size={14} />
            </UiNode>
          )}
          <MuteToggle boardId={board.id} />
          {isOwner && (
            <button
              type="button"
              className="btn btn-ghost inline-flex items-center gap-1.5"
              onClick={() => setSettingsOpen(true)}
              aria-label="Board settings"
              title="Board settings"
            >
              <IconCog size={14} />
            </button>
          )}
          <UiNode
            as="button"
            name="board-share-button"
            entity="board"
            entityId={board.id}
            action="share_open"
            className="btn btn-ghost inline-flex items-center gap-1.5"
            onClick={() => setShareOpen(true)}
            title="Share board"
          >
            <IconShare size={14} />
            <span className="hidden sm:inline">Share</span>
          </UiNode>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-[22px] font-medium tracking-tight title-gradient sm:text-[24px]">
              {board.name}
            </h1>
            <span className="chip chip-accent">{phaseLabel(board.phase)}</span>
            {/* Show 'Guest on {Workspace}' when this board belongs to
                a different workspace than the viewer's home. Makes
                the cross-workspace context unambiguous. */}
            {board.workspace && board.workspace.id !== me.tenantId && (
              <span
                className="chip"
                title="You're a guest on this board — it lives in another workspace."
              >
                Guest on {board.workspace.name || 'another workspace'}
              </span>
            )}
          </div>
          {(board.tagline || board.workspace) && (
            <p className="mt-1 text-sm text-cream-3">
              {board.tagline}
              {board.tagline && board.workspace ? ' · ' : ''}
              {board.workspace && (
                <span className="text-cream-4">{board.workspace.name}</span>
              )}
            </p>
          )}
        </div>

        {canAdvance && (
          <UiNode
            as="button"
            name="board-phase-advance-button"
            entity="board"
            entityId={board.id}
            action="phase_advance"
            className="btn btn-primary"
            onClick={onAdvance}
          >
            Move to {phaseLabel(nextPhase(board.phase)!)}
          </UiNode>
        )}
      </div>

      {/* Progress bar — segments are clickable for the owner so they can
          jump back to fix things mid-flow (people change their minds). */}
      <PhaseProgress
        phase={board.phase}
        canChange={board.role === 'owner'}
        onSelect={async (p) => {
          await api.setPhase(board.id, p);
          onBoardChange({ ...board, phase: p });
        }}
      />

      <BoardSettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        board={board}
        onBoardChange={onBoardChange}
      />
      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        board={board}
        me={me}
        onBoardChange={onBoardChange}
      />
      <LobbyInbox
        open={lobbyOpen}
        onClose={() => setLobbyOpen(false)}
        board={board}
      />
    </div>
  );
}

function MuteToggle({ boardId }: { boardId: string }) {
  const [muted, setMuted] = useState<boolean | null>(null);
  useEffect(() => {
    let cancel = false;
    void api
      .isBoardMuted(boardId)
      .then((r) => {
        if (!cancel) setMuted(r.muted);
      })
      .catch(() => undefined);
    return () => {
      cancel = true;
    };
  }, [boardId]);
  if (muted === null) return null;
  const label = muted ? "Notifications muted — click to turn back on" : "Mute this board's notifications";
  return (
    <UiNode
      as="button"
      name="board-mute-toggle"
      entity="board_mute"
      entityId={boardId}
      action="toggle"
      state={muted ? 'on' : 'off'}
      type="button"
      className="btn btn-ghost inline-flex items-center gap-1.5"
      onClick={async () => {
        const next = !muted;
        setMuted(next); // optimistic
        try {
          if (next) await api.muteBoard(boardId);
          else await api.unmuteBoard(boardId);
        } catch {
          setMuted(!next); // revert
        }
      }}
      aria-label={label}
      title={label}
      aria-pressed={muted}
    >
      {muted ? <IconBellOff size={14} /> : <IconBell size={14} />}
    </UiNode>
  );
}

function PhaseProgress({
  phase,
  canChange,
  onSelect,
}: {
  phase: Phase;
  canChange: boolean;
  onSelect: (p: Phase) => void;
}) {
  const visible = ALL_PHASES.filter((p): p is Exclude<Phase, 'archived'> => p !== 'archived');
  const idx = visible.indexOf(phase as Exclude<Phase, 'archived'>);
  // Each step is now a tall button that contains both the bar and
  // the label, so the whole vertical area (~44px) is the click target.
  // The bar inside stays slim as a visual indicator only.
  return (
    <ol className="flex flex-col" aria-label="Board phase progress">
      <div className="flex items-stretch gap-1.5">
        {visible.map((p, i) => {
          const active = i === idx;
          const done = i < idx;
          const Tag = canChange ? 'button' : 'div';
          return (
            <li
              key={p}
              className="contents"
            >
              <Tag
                type={canChange ? 'button' : undefined}
                aria-current={active ? 'step' : undefined}
                aria-label={phaseLabel(p)}
                className="group flex min-h-[44px] flex-col items-stretch justify-start gap-1.5 rounded-md py-1.5 transition hover:bg-ink-3"
                style={{
                  flex: active ? '1.8 1 0' : '1 1 0',
                  cursor: canChange ? 'pointer' : 'default',
                }}
                onClick={
                  canChange
                    ? () => {
                        if (p !== phase) onSelect(p);
                      }
                    : undefined
                }
                title={phaseLabel(p)}
              >
                <span
                  className="block rounded-full transition-all"
                  style={{
                    height: active ? '6px' : '4px',
                    background: active
                      ? 'var(--color-clove)'
                      : done
                        ? 'var(--color-cream-3)'
                        : 'var(--color-ink-rim)',
                  }}
                />
                <span
                  className="block truncate px-0.5 text-center text-[10px] uppercase tracking-wide"
                  style={{
                    color: active
                      ? 'var(--color-cream-2)'
                      : done
                        ? 'var(--color-cream-3)'
                        : 'var(--color-cream-4)',
                  }}
                >
                  {phaseLabel(p)}
                </span>
              </Tag>
            </li>
          );
        })}
      </div>
    </ol>
  );
}

function PresenceIndicator({
  state,
  presence,
  selfId,
}: {
  state: 'connecting' | 'open' | 'closed';
  presence: { id: string; name: string; avatarHue: number; avatarUrl: string | null }[];
  selfId: string;
}) {
  // Show OTHERS only — the viewer knows they're here.
  const others = presence.filter((p) => p.id !== selfId);
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{
          background:
            state === 'open'
              ? 'var(--color-leaf)'
              : state === 'connecting'
                ? 'var(--color-cream-3)'
                : 'var(--color-cream-4)',
        }}
        aria-hidden
      />
      <span className="text-xs text-cream-3">
        {state === 'open' ? 'Live' : state === 'connecting' ? 'Connecting…' : 'Offline'}
      </span>
      {others.length > 0 && <AvatarStack users={others} size={22} max={4} />}
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="h-6 w-44 animate-pulse rounded bg-ink-3" />
      <div className="h-1.5 w-full animate-pulse rounded-full bg-ink-3" />
      <div className="panel h-40 animate-pulse" />
    </div>
  );
}
